import React, { useEffect } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { useQuery } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { fg } from '@confluence/feature-gating';
import { ssrContentStateManager } from '@confluence/ssr-utilities';

import { LazyEditorContentStateComponent } from '../lazyLoadables';

import { ContentStateStateContainer, defaultValues } from './ContentStateStateContainer';
import type {
	EditorContentStateQuery as EditorContentStateQueryType,
	EditorContentStateQueryVariables,
} from './__types__/EditorContentStateQuery';
import { EditorContentStateQuery } from './EditorContentStateQuery.graphql';
import type { EditorContentStateProps } from './types';

const EditorContentStateDataGetter = ({
	contentId,
	contentType,
	contentStateState,
	isPublishingContent,
	userOffline,
	onContentStateMenuToggle,
	floatingToolbarButtonStyle,
	renderPlusButtonEmptyState,
	isDetailsPanel,
}: EditorContentStateProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isCurrentPageLive = useIsCurrentPageLive();

	const { data } = useQuery<EditorContentStateQueryType, EditorContentStateQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		EditorContentStateQuery,
		{
			fetchPolicy: Boolean(window.__SSR_RENDERED__) ? 'cache-first' : 'network-only',
			variables: {
				contentId,
				isDraft: !isCurrentPageLive,
			},
			skip: !contentStateState.state.shouldRunContentStateEffect,
		},
	);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && data && fg('confluence_populate_content_state_from_ssr')) {
		ssrContentStateManager.setContentStateData(data);
	}

	useEffect(() => {
		if (data && contentStateState.state.shouldRunContentStateEffect) {
			const newContentStateValues = Object.assign({}, defaultValues);
			/* EditorContentState is rendered in the breadcrumbs & title area content components based on if a page has a content
      state or not. The re-render from the change in parent causes the useEffect and query to run again, resetting the values.
      To prevent this, we have the shouldRunContentStateEffect state. If we are inside the if statement, it means it's the first time
      we're fetching (aka first page load). Since we already ran the effect once, don't do it again since otherwise it
      will cause a values reset. */
			newContentStateValues.shouldRunContentStateEffect = false;
			newContentStateValues.contentState = data?.singleContent?.contentState || null;
			newContentStateValues.defaultStatesArray =
				data?.availableContentStates?.spaceContentStates || [];
			newContentStateValues.customStatesArray =
				data?.availableContentStates?.customContentStates || [];
			// If an admin has turned off a toggle, BE returns us the value as null
			// If an empty array is returned [], means the feature is still enabled - there's just no values in there.
			// if both return as null it means the admin disabled content states
			const isSuggestedContentStatesDisabled = !data?.availableContentStates?.spaceContentStates;
			const isCustomContentStatesDisabled = !data?.availableContentStates?.customContentStates;
			const isContentStatesDisabled =
				isSuggestedContentStatesDisabled && isCustomContentStatesDisabled;
			if (isContentStatesDisabled) {
				newContentStateValues.isContentStateEnabled = false;
			} else if (isSuggestedContentStatesDisabled) {
				// if spaceContentStates is null, admin disabled suggested
				newContentStateValues.isSuggestedEnabled = false;
			} else if (isCustomContentStatesDisabled) {
				// if customContentStates is null, admin disabled custom
				newContentStateValues.isCustomEnabled = false;
			}
			void contentStateState.setInitialValues(newContentStateValues);
		}
	}, [data, contentStateState]);

	if (isPublishingContent) {
		// isPublishingContent is an optional prop that we only pass to this component if a page has a content state.
		// Therefore if it's true, means we're publishing a page with a content state so fire the analytic event
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'publishedWithContentState',
				actionSubject: 'page',
				objectId: contentId,
				source: 'editorContentState',
			},
		}).fire();
	}

	return (
		<LazyEditorContentStateComponent
			contentId={contentId}
			contentType={contentType}
			contentStateState={contentStateState}
			userOffline={userOffline}
			onContentStateMenuToggle={onContentStateMenuToggle}
			floatingToolbarButtonStyle={floatingToolbarButtonStyle}
			renderPlusButtonEmptyState={renderPlusButtonEmptyState}
			isDetailsPanel={isDetailsPanel}
		/>
	);
};

const EditorContentStateWithErrorBoundary = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(EditorContentStateDataGetter);

export const EditorContentState = (props: any) => (
	<Subscribe to={[ContentStateStateContainer]}>
		{(contentStateState: ContentStateStateContainer) => (
			<EditorContentStateWithErrorBoundary {...props} contentStateState={contentStateState} />
		)}
	</Subscribe>
);
